import { hasBelt, addItemToBelt } from './BeltItemSystem';
import {
  AssemblerInventory,
  Position,
  PlayerFacing,
  ItemType,
  Building,
  BuildingType,
} from './constants';
import {
  AssemblerInventoryLayer,
  BuildingLayer,
  BeltContentLayer,
  getAssemblerInventory,
  setAssemblerInventory,
  getBuildingAtPosition,
  getBuildingSize,
  getMaxInputOutputsMultiplier,
} from './GameState';
import { TickSystem, getCurrentTick } from './hooks/useGameTick';
import { buildingLogger } from './Logger';
import {
  ResearchState,
  getAssemblerSpeedMultiplier,
  getFactorySpeedMultiplier,
  getOutputCapacityMultiplier,
  getBeltCapacityMultiplier,
} from './ResearchSystem';
import { getBeltDirectionFromCharacter } from './SmartBeltPlacement';
import { secondsToTicks } from './utils/TickIntervals';

const DEFAULT_MAX_IO_MULTIPLIER = 1;

const resolveCurrentTick = (tickSystem?: TickSystem): number => {
  return tickSystem?.getCurrentTick() ?? getCurrentTick();
};

const parsePositionKey = (posKey: string): Position | null => {
  const [xStr, yStr] = posKey.split(',');
  if (!xStr || !yStr) {
    return null;
  }

  const x = Number.parseInt(xStr, 10);
  const y = Number.parseInt(yStr, 10);

  if (Number.isNaN(x) || Number.isNaN(y)) {
    return null;
  }

  return { x, y };
};

// Check if an assembler has enough inputs to start crafting
export const canStartCrafting = (
  inventory: AssemblerInventory,
  maxInputOutputsMultiplier: number = DEFAULT_MAX_IO_MULTIPLIER
): boolean => {
  if (!inventory.recipe) {
    return false;
  }

  // Check if we have enough of each input ingredient
  for (const input of inventory.recipe.inputs) {
    const availableCount = inventory.items[input.item] || 0;
    if (availableCount < input.count) {
      return false;
    }
  }

  // Ensure there is enough output capacity before starting
  for (const output of inventory.recipe.outputs) {
    const currentCount = inventory.items[output.item] || 0;
    const capacity = output.count * maxInputOutputsMultiplier;
    if (currentCount + output.count > capacity) {
      return false;
    }
  }

  return true;
};

// Get adjacent positions for a multi-tile building
const getAdjacentPositionsForBuilding = (
  centerPos: Position,
  building: Building
): { position: Position; direction: PlayerFacing }[] => {
  const size = getBuildingSize(building);
  const adjacentPositions: { position: Position; direction: PlayerFacing }[] =
    [];

  // Calculate the top-left corner of the building
  const topLeftX = centerPos.x - Math.floor(size.width / 2);
  const topLeftY = centerPos.y - Math.floor(size.height / 2);

  // Get positions around the perimeter of the building
  for (let x = topLeftX - 1; x <= topLeftX + size.width; x++) {
    for (let y = topLeftY - 1; y <= topLeftY + size.height; y++) {
      // Skip positions inside the building
      if (
        x >= topLeftX &&
        x < topLeftX + size.width &&
        y >= topLeftY &&
        y < topLeftY + size.height
      ) {
        continue;
      }

      // Determine direction from building to adjacent position
      let direction: PlayerFacing | null = null;

      if (x < topLeftX) direction = 'left';
      else if (x >= topLeftX + size.width) direction = 'right';
      else if (y < topLeftY) direction = 'up';
      else if (y >= topLeftY + size.height) direction = 'down';

      if (direction) {
        adjacentPositions.push({
          position: { x, y },
          direction,
        });
      }
    }
  }

  return adjacentPositions;
};

// Check if a belt at the given position can accept output from an assembler
const canBeltAcceptOutput = (
  beltPos: Position,
  expectedDirection: PlayerFacing,
  buildingLayer: BuildingLayer
): boolean => {
  if (!hasBelt(beltPos, buildingLayer)) return false;

  // Use proper helper function to get building at position
  const { building } = getBuildingAtPosition(
    beltPos.x,
    beltPos.y,
    buildingLayer
  );
  if (!building) return false;

  const beltDirection = getBeltDirectionFromCharacter(buildingLayer, beltPos);
  if (!beltDirection) return false;

  // Belt must be facing away from the assembler (same direction as the expected output direction)
  return beltDirection === expectedDirection;
};

// Try to output items from assembler to adjacent belts
const tryOutputToBelts = (
  assemblerPos: Position,
  inventory: AssemblerInventory,
  buildingLayer: BuildingLayer,
  beltContentLayer: BeltContentLayer,
  researchState: ResearchState,
  generateId: () => string,
  onOutputItem?: (itemType: ItemType, amount: number) => void
): {
  updatedInventory: AssemblerInventory;
  updatedBeltContent: BeltContentLayer;
} => {
  let updatedInventory = { ...inventory };
  let updatedBeltContent = { ...beltContentLayer };

  // Get the assembler building to determine its size
  const { building } = getBuildingAtPosition(
    assemblerPos.x,
    assemblerPos.y,
    buildingLayer
  );
  if (!building) return { updatedInventory, updatedBeltContent };

  const beltCapacityMultiplier = getBeltCapacityMultiplier(researchState);

  const adjacentPositions = getAdjacentPositionsForBuilding(
    assemblerPos,
    building
  );

  // Try to output each item type that has quantity > 0 and is a recipe output
  for (const [itemType, quantity] of Object.entries(updatedInventory.items)) {
    if (quantity <= 0) continue;

    // Only output items that are products of the current recipe
    if (!inventory.recipe) continue;
    const isRecipeOutput = inventory.recipe.outputs.some(
      output => output.item === itemType
    );
    if (!isRecipeOutput) continue;

    // Find a valid output belt for this item type
    for (const { position: beltPos, direction } of adjacentPositions) {
      if (canBeltAcceptOutput(beltPos, direction, buildingLayer)) {
        // Check if belt has space
        const beltKey = `${beltPos.x},${beltPos.y}`;
        const currentBeltItems = updatedBeltContent[beltKey] || [];
        const existingStack = currentBeltItems[0];
        const existingCount = existingStack?.count ?? (existingStack ? 1 : 0);
        const canCreateStack = !existingStack;
        const canStackExisting =
          existingStack?.item === itemType &&
          existingCount < beltCapacityMultiplier;

        if (!canCreateStack && !canStackExisting) {
          continue;
        }

        const nextBeltContent = addItemToBelt(
          beltPos,
          itemType as ItemType,
          updatedBeltContent,
          beltCapacityMultiplier,
          generateId
        );

        if (nextBeltContent === updatedBeltContent) {
          continue;
        }

        updatedBeltContent = nextBeltContent;
        updatedInventory = {
          ...updatedInventory,
          items: {
            ...updatedInventory.items,
            [itemType]: quantity - 1,
          },
        };

        onOutputItem?.(itemType as ItemType, 1);

        buildingLogger.info('Assembler output item to belt', {
          assemblerPos: `${assemblerPos.x},${assemblerPos.y}`,
          beltPos: `${beltPos.x},${beltPos.y}`,
          itemType,
          remainingInAssembler: quantity - 1,
        });

        break; // Only output one item per tick per item type
      }
    }
  }

  return { updatedInventory, updatedBeltContent };
};

// Helper function to determine if an assembler is idle and ready to start crafting
export const isAssemblerIdle = (
  inventory: AssemblerInventory | null | undefined,
  maxInputOutputsMultiplier: number = DEFAULT_MAX_IO_MULTIPLIER
): boolean => {
  if (!inventory) {
    return false;
  }

  const hasRecipe = !!inventory.recipe;
  const notCurrentlyCrafting = inventory.craftingStartTick === null;
  const hasIngredients =
    hasRecipe && canStartCrafting(inventory, maxInputOutputsMultiplier);

  return hasRecipe && notCurrentlyCrafting && hasIngredients;
};

// Start crafting process for an assembler
export const startCrafting = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  position: { x: number; y: number },
  researchState: ResearchState,
  tickSystem?: TickSystem
): AssemblerInventoryLayer => {
  const currentInventory = getAssemblerInventory(
    assemblerInventoryLayer,
    buildingLayer,
    position
  );

  const { building } = getBuildingAtPosition(
    position.x,
    position.y,
    buildingLayer
  );
  const maxInputOutputsMultiplier =
    building?.maxInputOutputsMultiplier ?? DEFAULT_MAX_IO_MULTIPLIER;
  const researchMultiplier = getOutputCapacityMultiplier(researchState);
  const effectiveMultiplier = maxInputOutputsMultiplier * researchMultiplier;

  if (
    !currentInventory ||
    !canStartCrafting(currentInventory, effectiveMultiplier)
  ) {
    return assemblerInventoryLayer;
  }

  // Consume input ingredients
  const newItems = { ...currentInventory.items };
  const recipe = currentInventory.recipe;
  if (!recipe) {
    throw new Error('Recipe is required to start crafting');
  }

  for (const input of recipe.inputs) {
    newItems[input.item] = (newItems[input.item] || 0) - input.count;
  }

  buildingLogger.info('Started crafting', {
    position: `${position.x},${position.y}`,
    recipe: recipe.name,
    duration: recipe.craftingTime,
  });

  const updatedInventory = {
    ...currentInventory,
    items: newItems,
    craftingProgress: 0,
    craftingStartTick: resolveCurrentTick(tickSystem),
  };

  return setAssemblerInventory(
    assemblerInventoryLayer,
    buildingLayer,
    position,
    updatedInventory
  );
};

// Update crafting progress for all assemblers
export const updateCraftingProgress = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  beltContentLayer: BeltContentLayer,
  researchState: ResearchState,
  powerSatisfaction: number,
  options: {
    tickSystem?: TickSystem;
    generateItemId?: () => string;
    onOutputItem?: (itemType: ItemType, amount: number) => void;
  } = {}
): {
  updatedAssemblerInventory: AssemblerInventoryLayer;
  updatedBeltContent: BeltContentLayer;
} => {
  const { tickSystem, generateItemId, onOutputItem } = options;
  let hasUpdates = false;
  const updatedLayer = { ...assemblerInventoryLayer };
  let updatedBeltContent = { ...beltContentLayer };

  const clampedPower = Math.max(powerSatisfaction, 0);

  Object.keys(assemblerInventoryLayer).forEach(posKey => {
    const baseInventory = assemblerInventoryLayer[posKey];
    if (!baseInventory || !baseInventory.recipe) {
      return;
    }

    const assemblerPos = parsePositionKey(posKey);
    if (!assemblerPos) {
      return;
    }

    const maxInputOutputsMultiplier = getMaxInputOutputsMultiplier(
      assemblerPos,
      buildingLayer,
      researchState
    );

    let workingInventory = updatedLayer[posKey] ?? baseInventory;

    const recipe = workingInventory.recipe;
    if (!recipe) {
      return;
    }

    const hasOutputItems = recipe.outputs.some(
      output => (workingInventory.items[output.item] || 0) > 0
    );

    if (hasOutputItems) {
      const outputResult = tryOutputToBelts(
        assemblerPos,
        workingInventory,
        buildingLayer,
        updatedBeltContent,
        researchState,
        generateItemId ??
          (() =>
            `assembler_output_${resolveCurrentTick(tickSystem)}_${Math.random()
              .toString(36)
              .slice(2)}`),
        onOutputItem
      );

      if (outputResult.updatedInventory !== workingInventory) {
        hasUpdates = true;
        updatedLayer[posKey] = outputResult.updatedInventory;
        workingInventory = outputResult.updatedInventory;
      }

      updatedBeltContent = outputResult.updatedBeltContent;
    }

    workingInventory = updatedLayer[posKey] ?? workingInventory;

    if (workingInventory.craftingStartTick === null) {
      return;
    }

    const currentTick = resolveCurrentTick(tickSystem);
    const { building } = getBuildingAtPosition(
      assemblerPos.x,
      assemblerPos.y,
      buildingLayer
    );

    const baseSpeedMultiplier =
      building?.id === BuildingType.FACTORY
        ? getFactorySpeedMultiplier(researchState)
        : getAssemblerSpeedMultiplier(researchState);

    const effectiveSpeedMultiplier =
      Math.max(baseSpeedMultiplier, 0) * clampedPower;
    const baseDurationTicks = Math.max(1, secondsToTicks(recipe.craftingTime));
    const lastUpdateTick = workingInventory.craftingStartTick ?? currentTick;
    const deltaTicks = Math.max(0, currentTick - lastUpdateTick);

    if (effectiveSpeedMultiplier <= 0 || deltaTicks <= 0) {
      if (workingInventory.craftingStartTick !== currentTick) {
        updatedLayer[posKey] = {
          ...workingInventory,
          craftingStartTick: currentTick,
        };
        hasUpdates = true;
      }
      return;
    }

    const progressIncrement =
      (deltaTicks * effectiveSpeedMultiplier) / baseDurationTicks;
    const currentProgress = workingInventory.craftingProgress || 0;
    const newProgress = Math.min(1, currentProgress + progressIncrement);

    if (newProgress >= 1) {
      const newItems = {
        ...(updatedLayer[posKey]?.items || workingInventory.items),
      };
      for (const output of recipe.outputs) {
        const maxCapacity = output.count * maxInputOutputsMultiplier;
        const existing = newItems[output.item] || 0;
        newItems[output.item] = Math.min(maxCapacity, existing + output.count);
      }

      buildingLogger.info('Completed crafting', {
        position: posKey,
        recipe: recipe.name,
        outputs: recipe.outputs,
      });

      const afterCrafting: AssemblerInventory = {
        ...(updatedLayer[posKey] || workingInventory),
        items: newItems,
        craftingProgress: 0,
        craftingStartTick: null,
      };

      updatedLayer[posKey] = afterCrafting;
      hasUpdates = true;

      const refreshedInventory = updatedLayer[posKey];
      if (
        refreshedInventory &&
        canStartCrafting(refreshedInventory, maxInputOutputsMultiplier)
      ) {
        buildingLogger.debug('Auto-starting next craft', {
          position: posKey,
        });
        const nextItems = { ...refreshedInventory.items };
        const nextRecipe = refreshedInventory.recipe;
        if (nextRecipe) {
          for (const input of nextRecipe.inputs) {
            nextItems[input.item] = (nextItems[input.item] || 0) - input.count;
          }

          updatedLayer[posKey] = {
            ...refreshedInventory,
            items: nextItems,
            craftingProgress: 0,
            craftingStartTick: resolveCurrentTick(tickSystem),
          };
        }
      }
    } else if (newProgress !== workingInventory.craftingProgress) {
      updatedLayer[posKey] = {
        ...workingInventory,
        craftingProgress: newProgress,
        craftingStartTick: currentTick,
      };
      hasUpdates = true;
    } else if (workingInventory.craftingStartTick !== currentTick) {
      updatedLayer[posKey] = {
        ...workingInventory,
        craftingStartTick: currentTick,
      };
      hasUpdates = true;
    }
  });

  return {
    updatedAssemblerInventory: hasUpdates
      ? updatedLayer
      : assemblerInventoryLayer,
    updatedBeltContent,
  };
};

// Process crafting for all assemblers - start new crafts and update progress
export const processAssemblerCrafting = (
  assemblerInventoryLayer: AssemblerInventoryLayer,
  buildingLayer: BuildingLayer,
  beltContentLayer: BeltContentLayer,
  researchState: ResearchState,
  powerSatisfaction: number,
  options: {
    tickSystem?: TickSystem;
    generateItemId?: () => string;
    onOutputItem?: (itemType: ItemType, amount: number) => void;
  } = {}
): {
  updatedAssemblerInventory: AssemblerInventoryLayer;
  updatedBeltContent: BeltContentLayer;
} => {
  const { tickSystem, generateItemId, onOutputItem } = options;
  let currentLayer = assemblerInventoryLayer;

  const assemblerCount = Object.keys(currentLayer).length;

  if (assemblerCount > 0) {
    buildingLogger.debug('Processing assembler crafting', {
      assemblerCount,
      positions: Object.keys(currentLayer),
    });
  }

  // First, update all crafting progress and handle outputs
  const progressResult = updateCraftingProgress(
    currentLayer,
    buildingLayer,
    beltContentLayer,
    researchState,
    powerSatisfaction,
    {
      ...(tickSystem ? { tickSystem } : {}),
      ...(generateItemId ? { generateItemId } : {}),
      ...(onOutputItem ? { onOutputItem } : {}),
    }
  );
  currentLayer = progressResult.updatedAssemblerInventory;
  const currentBeltContent = progressResult.updatedBeltContent;

  // Then, start new crafts for idle assemblers with sufficient inputs
  Object.keys(currentLayer).forEach(posKey => {
    const inventory = currentLayer[posKey];

    const assemblerPos = parsePositionKey(posKey);
    if (!assemblerPos) {
      buildingLogger.error('Invalid assembler inventory position key', {
        position: posKey,
      });
      return;
    }

    const maxInputOutputsMultiplier = getMaxInputOutputsMultiplier(
      assemblerPos,
      buildingLayer,
      researchState
    );

    if (!isAssemblerIdle(inventory, maxInputOutputsMultiplier)) {
      return;
    }

    // TypeScript guard: if isAssemblerIdle returned true, inventory must be defined
    if (!inventory) {
      return;
    }

    const canStart = canStartCrafting(inventory, maxInputOutputsMultiplier);

    buildingLogger.debug('Checking if assembler can start crafting', {
      position: posKey,
      hasRecipe: !!inventory.recipe,
      recipeName: inventory.recipe?.name,
      canStart,
    });

    if (canStart) {
      const position = assemblerPos;
      buildingLogger.info('Starting crafting for assembler', {
        position: posKey,
        recipe: inventory.recipe?.name,
      });
      currentLayer = startCrafting(
        currentLayer,
        buildingLayer,
        position,
        researchState,
        tickSystem
      );
    }
  });

  return {
    updatedAssemblerInventory: currentLayer,
    updatedBeltContent: currentBeltContent,
  };
};
